module Client.Admin.AddUser

open Shared
open Client
open System
open Elmish
open Illy.SafeStack
open Illy.SafeStack.Client
open Fable.React 
open Fable.React.Props
open Shared.Admin
open Shared.Admin.AddUser
open Shared.Zxcvbn

type Model = 
    {
        Licenses: LicenseCounts
        Loading: bool
        Username: Text.Model
        EditFields: EditUser.EditUser.EditFields.Model
        Password: ScorablePassword.Model
        ConfirmPassword: Text.Model
        Applications: ManageApplications.Edit.Application.Model list 
        GeneralError: string option
        PasswordConfig: PasswordConfig
        DebouncePasswordZxcvbnListUpdate: unit Debounce.Model
        Saving: bool
    }

let toDTO (model: Model) = 
    {
        Username = Text.toString model.Username
        EditFields = EditUser.EditUser.EditFields.toDTO model.EditFields
        Password = ScorablePassword.toString model.Password
        ConfirmPassword = Text.toString model.ConfirmPassword
        Applications = model.Applications |> List.map ManageApplications.Edit.Application.toDTO
    }

module LicenseCounts =
    let blank =
        {
            Used = 0
            Total = 0
        }

module Validations = 
    open Illy.SafeStack.Validation
    open Illy.SafeStack.CompositionalValidation
    open Shared.Admin
    open Shared.Admin.AddUser.Validations
    open Shared.PasswordReset.ResetPassword.Validations
    open Shared.Validations.PasswordStrength.Validations

    let resetValidations (model: Model) = 
        {model with 
            Username = Text.resetValidations model.Username
            EditFields = EditUser.EditUser.EditFields.Validations.resetValidations model.EditFields
            Password = ScorablePassword.resetValidations model.Password
            ConfirmPassword = Text.resetValidations model.ConfirmPassword
            GeneralError = None
        }

    let dispatchCreateUserValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            Browser.Dom.console.log("validations failed:", err)
            let errors, editFields = 
                err 
                |> List.fold
                    (fun (acc, editFields) x -> 
                        match x with 
                        | UsernameValidations v -> addToAcc acc 0 (UsernameValidations.toString v), editFields
                        | PasswordValidations v -> addToAcc acc 1 (PasswordStrengthValidations.toString v), editFields
                        | ConfirmPasswordValidations v -> addToAcc acc 2 (ConfirmPasswordValidations.toString v), editFields
                        | GeneralValidations v -> addToAcc acc 3 (GeneralValidations.toString v), editFields
                        | EditFields v -> acc, v :: editFields)
                    ([| for x in 0..3 do yield [] |], [])

            {model with 
                Username = Text.setValidations model.Username (concatOptional errors.[0]) None
                EditFields = EditUser.EditUser.EditFields.Validations.dispatchValidations model.EditFields (ValidationResult.Failed (editFields, []))
                Password = ScorablePassword.setValidations model.Password (concatOptional errors.[1]) None
                ConfirmPassword = Text.setValidations model.ConfirmPassword (concatOptional errors.[2]) None
                GeneralError = (concatOptional errors.[3])
            }

    let dispatchAppValidations model = 
        let model = resetValidations model 
        function 
        | ValidationResult.Passed -> model 
        | ValidationResult.Warned _ -> failwith "Did not expect warnings"
        | ValidationResult.Failed (err, _) ->
            let errors = List.map ManageApplications.Validations.ManageApplicationsValidations.toString err
            {model with GeneralError = concatOptional errors}

type Msg = 
    | ApiError of ApiError
    | SaveResult of ComposedFailureReason option
    | UsernameMsg of Text.Msg
    | EditFieldsMsg of EditUser.EditUser.EditFields.Msg
    | PasswordMsg of ScorablePassword.Msg
    | ConfirmPasswordMsg of Text.Msg
    | ApplicationMsg of ManageApplications.Edit.Application.Identifier * ManageApplications.Edit.Application.Msg
    | CloseOverlay  
    | SaveClicked 
    | InitDataLoaded of InitData
    | DebouncePasswordZxcvbnListUpdateMsg of unit Debounce.Msg
    | UpdatePasswordConfig of unit

type Event = 
    | CloseOverlay 
    | ApiError of ApiError 
    | SaveSuccessful of notification: string 

module Initialisations =
    let initUsername value =
        Text.CustomSettings.defaultOptions()
        |> Text.init "Username" "Username" (Some "Enter Username") 250. true value

    let initPassword (config: PasswordConfig) value =
        ScorablePassword.init 
            {ScorablePassword.Settings.defaults with Config = config} 
            value

    let initConfirmPassword value =
        {Text.CustomSettings.defaultOptions() with TextType = Text.TextType.Password}
        |> Text.init "ConfirmPassword" "Confirm Password" (Some "Confirm Password") 250. true value

let init () = 
    {
        Licenses = LicenseCounts.blank
        Loading = true
        Username = Initialisations.initUsername None
        EditFields = EditUser.EditUser.EditFields.ofDTO EditUser.EditUser.AdditionalFieldsConfig.defaults EditUser.EditUser.DTO.blank
        Password = Initialisations.initPassword PasswordConfig.defaults None
        ConfirmPassword = Initialisations.initConfirmPassword None
        Applications = []
        GeneralError = None
        PasswordConfig = PasswordConfig.defaults
        Saving = false
        DebouncePasswordZxcvbnListUpdate = Debounce.init (TimeSpan.FromMilliseconds 300.) ()
    }, Api.secureCall<IAddUserApi,_,_,_> SecuredRoute.Admin (fun api -> api.loadInitData) () InitDataLoaded Msg.ApiError

let dispatchApplicationMsg model appIdentifier msg = 
    let m, c = 
        ListItem.dispatchMsgInList 
            appIdentifier 
            ManageApplications.Edit.Application.getIdentifier 
            model.Applications
            ManageApplications.Edit.Application.update
            Cmd.none
            msg
    {model with Applications = m}, c |> Cmd.map (fun x -> ApplicationMsg(appIdentifier, x)) |> SideEffect.Cmd

let update msg model = 
    match msg with 
    | InitDataLoaded d -> 
        {model with 
            Applications = d.Applications |> List.map ManageApplications.Edit.Application.ofDTO
            EditFields = EditUser.EditUser.EditFields.ofDTO d.EditFieldsConfig EditUser.EditUser.DTO.blank
            Password = Initialisations.initPassword d.PasswordConfig None
            Licenses = d.Licenses
            Loading = false
        }, SideEffect.none
    | UsernameMsg cMsg ->
        let m, c = Text.update cMsg model.Username
        {model with Username = m}, Cmd.map UsernameMsg c |> SideEffect.Cmd
    | EditFieldsMsg eMsg -> 
        let m, c = EditUser.EditUser.EditFields.update eMsg model.EditFields
        {model with EditFields = m}, Cmd.batch [
            Cmd.map EditFieldsMsg c
            Debounce.inputCmd () DebouncePasswordZxcvbnListUpdateMsg
        ] |> SideEffect.Cmd
    | PasswordMsg cMsg ->
        let m, c = ScorablePassword.update cMsg model.Password
        {model with Password = m}, Cmd.map PasswordMsg c |> SideEffect.Cmd
    | ConfirmPasswordMsg cMsg ->
        let m, c = Text.update cMsg model.ConfirmPassword
        {model with ConfirmPassword = m}, Cmd.map ConfirmPasswordMsg c |> SideEffect.Cmd
    | ApplicationMsg (aId, aMsg) -> dispatchApplicationMsg model aId aMsg
    | Msg.ApiError err -> {model with Saving = false}, err |> ApiError |> SideEffect.Event 
    | Msg.CloseOverlay -> model, CloseOverlay |> SideEffect.Event 
    | SaveClicked -> 
        let dto = toDTO model 
        let vResult =
            Validations.validateDTO
                Zxcvbn.scorePassword 
                model.PasswordConfig
                model.Licenses
                []
                dto
        if Validation.isSuccessful false vResult then 
            {model with Saving = true},
            Api.secureCall<IAddUserApi,_,_,_> SecuredRoute.Admin (fun api -> api.create) dto SaveResult Msg.ApiError
            |> SideEffect.Cmd
        else 
            Validations.dispatchCreateUserValidations model vResult, SideEffect.none
    | SaveResult reason -> 
        match reason with 
        | None -> model, "User was created successfully." |> Event.SaveSuccessful |> SideEffect.Event
        | Some (ComposedFailureReason.CreateUser (FailureReason.FailedValidations v)) -> 
            Validations.dispatchCreateUserValidations {model with Saving = false} v, SideEffect.none
        | Some (ComposedFailureReason.Apps reason) -> 
            match reason with 
            | ManageApplications.FailureReason.NothingToDo -> 
                model, "User was created successfully." |> Event.SaveSuccessful |> SideEffect.Event
            | ManageApplications.FailureReason.FailedValidations v -> 
                Validations.dispatchAppValidations {model with Saving = false} v, SideEffect.none
    | DebouncePasswordZxcvbnListUpdateMsg dMsg -> 
        let m, c = 
            Debounce.updateWithCmd
                dMsg
                DebouncePasswordZxcvbnListUpdateMsg
                model.DebouncePasswordZxcvbnListUpdate
                (fun x -> 
                    { model with
                        DebouncePasswordZxcvbnListUpdate = x 
                    })
                (UpdatePasswordConfig >> Cmd.ofMsg)
        m, SideEffect.Cmd c
    | UpdatePasswordConfig _ -> 
        let dto = toDTO model
        let l = 
            [
                dto.EditFields.Forename
                dto.EditFields.Surname
                dto.Username
                dto.EditFields.TelephoneNumber
                dto.EditFields.Email
                dto.EditFields.AdditionalText1
            ]
            |> List.choose id 
            |> (@) model.PasswordConfig.ZxcvbnList
        model, l |> ScorablePassword.Msg.UpdateZxcvbnList |> PasswordMsg |> Cmd.ofMsg |> SideEffect.Cmd

let view model dispatch = 
    jQueryUI.modal "Add User" 50 Msg.CloseOverlay dispatch (Some 1000) [
        div [
            Id "add-user-content"
        ] [
            div [] [
                yield h2 [] [ str "User Details"] 
                yield Text.text model.Loading model.Username (UsernameMsg >> dispatch)
                yield! EditUser.EditUser.EditFields.view model.Loading model.EditFields (EditFieldsMsg >> dispatch)
                yield! ScorablePassword.view model.Loading model.Password (PasswordMsg >> dispatch)
                yield Text.text model.Loading model.ConfirmPassword (ConfirmPasswordMsg >> dispatch)
            ]

            div [
                ClassName "apps"
            ] [
                h2 [] [ str "Application Access"] 
                yield! 
                    if model.Loading then ManageApplications.Edit.skeletonScreen() else model.Applications
                    |> List.map (fun a -> ManageApplications.Edit.Application.view model.Loading a ((fun msg -> ApplicationMsg(ManageApplications.Edit.Application.getIdentifier a, msg)) >> dispatch))
            ]
        ]

        UI.renderError model.GeneralError

        yield! UI.saveControls (Some "Add User") false model.Saving SaveClicked Msg.CloseOverlay dispatch
    ]